import * as breakpoints from "../breakpoints";
import * as colors from "../constants/colors";
import * as languages from "../constants/languages";
import CertificateInfo from "./CertificateInfo";
import CertificateResolution from "./CertificateResolution";
import { Column, Container, Row } from "../grid";
import { english, spanish } from "../constants/strings";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import { StyleSheet, css } from "aphrodite";
import { format } from "date-fns";
import html2canvas from "html2canvas";
import FileSaver from "file-saver";
import bowser from "bowser";

const styles = StyleSheet.create({
	container: {
		paddingTop: 32,
		paddingLeft: 20,
		paddingRight: 20,
		paddingBottom: 50,
	},
	fullScreenContainer: {
		position: "relative",
		backgroundColor: colors.white,
		zIndex: 2,
		[breakpoints.mobileOnly]: {
			width: "100vw",
			paddingLeft: 39,
			paddingRight: 36,
			paddingBottom: 0,
			overflow: "auto",
		},
		width: "100%",
		[breakpoints.tabletAndUp]: {
			maxWidth: 580,
		},
		maxHeight: "100vh",

		"@media (max-height: 750px)": {
			height: "100vh",
		},
	},
	fullScreenCover: {
		position: "fixed",
		height: "100vh",
		width: "100vw",
		backgroundColor: colors.transparentBlack,
		top: 0,
		left: 0,
		display: "flex",
		[breakpoints.tabletAndUp]: {
			justifyContent: "center",
			alignItems: "center",
		},
		zIndex: 2,
		"@media print": {
			backgroundColor: colors.white,
			alignItems: "flex-start",
			pageBreakAfter: "auto",
		},
		"@media (max-height: 750px)": {
			alignItems: "flex-start",
			overflow: "auto",
		},
	},
	button: {
		border: `3px solid ${colors.gold2}`,
		color: colors.gold2,
		backgroundColor: colors.white,
		borderRadius: 20,
		paddingTop: 2,
		paddingBottom: 2,
		lineHeight: 2,
		fontSize: 15,
		fontFamily: "freight-sans-pro",
		fontWeight: "bold",
		width: 144,
	},
	buttonContainer: {
		display: "flex",
		justifyContent: "center",
		[breakpoints.mobileOnly]: {
			marginBottom: 20,
		},
	},
	buttonRow: {
		marginTop: 44,
		"@media print": {
			display: "none",
			pageBreakAfter: "auto",
		},
	},
	helpButtonContainer: {
		textAlign: "center",
		marginTop: 16,
	},
	certificateBorderTopContainer: {
		display: "flex",
		justifyContent: "center",
		marginBottom: 18,
	},
	certificateBorderTopImage: {
		width: 206,
	},
	certificateBorderBottomContainer: {
		display: "flex",
		justifyContent: "center",
		marginTop: 12,
	},
	certificateBorderBottomImage: {
		width: 234,
	},
	certificateInfoRow: {
		marginTop: 34,
		"@media print": {
			display: "flex",
		},
	},
	certificateInfo: {},
	courseImageContainer: {
		[breakpoints.mobileOnly]: {
			paddingTop: 20,
			textAlign: "center",
		},
	},
	instructionContainer: {
		marginTop: 28,
		marginBottom: 20,
		fontFamily: "freight-sans-pro",
		fontSize: 15,
		lineHeight: 1.33,
		color: colors.black7,
		textAlign: "center",
	},
	title: {
		fontSize: 45,
		fontFamily: "PopulaireLight",
		color: colors.purple,
		display: "flex",
		justifyContent: "center",
	},
	helpHeader: {
		fontFamily: "priori-sans",
		fontSize: 20,
	},
});

export default class Certificate extends PureComponent {
	static propTypes = {
		completionDate: PropTypes.string.isRequired,
		certificateNumber: PropTypes.string.isRequired,
		courseTitle: PropTypes.string.isRequired,
		courseImage: PropTypes.object,
		resolution: PropTypes.string.isRequired,
		resolutionPrompt: PropTypes.string.isRequired,
		language: PropTypes.string.isRequired,
	};

	constructor(props) {
		super(props);

		this.strings = props.language === languages.es ? spanish : english;
		this.state = {
			iPhoneHelpOpen: false,
		};
	}

	_checkPlatform = () => {
		if (bowser.ios) {
			this.setState({ iPhoneHelpOpen: true });
		} else {
			this._savePicture();
		}
	};

	_savePicture = () => {
		if (this.state.iPhoneHelpOpen) {
			this.setState({ iPhoneHelpOpen: false });
		}

		html2canvas(this._cert.getWrappedInstance()).then(canvas => {
			canvas.toBlob(blob => {
				FileSaver.saveAs(blob, "certificate.png");
			});
		});
	};

	_downloaddPdf = () => {
		window.print();
	};

	_print = () => {
		window.print();
	};

	_setRef = ref => {
		this._cert = ref;
	};

	_dontPropagate = e => {
		e.stopPropagation();
	};

	_dismiss = () => this.setState({ iPhoneHelpOpen: false });

	_renderIphoneHelpOverlay = () => {
		return (
			<div onClick={this._dismiss} className={css(styles.fullScreenCover)}>
				<div className={css(styles.fullScreenContainer)} onClick={this._dontPropagate}>
					<h3 className={css(styles.helpHeader)}>{this.strings.certificateIphoneHelp}</h3>
					<img
						alt="iphone save certificate instructions"
						width="100%"
						src="/images/iphone-help.png"
					/>
					<div className={css(styles.helpButtonContainer)}>
						<button
							className={css(styles.button, styles.helpButton)}
							onClick={this._savePicture}
						>
							{this.strings.CONTINUE}
						</button>
					</div>
				</div>
			</div>
		);
	};

	render() {
		return (
			<Container maxWidth={1024} ref={this._setRef}>
				{this.state.iPhoneHelpOpen ? this._renderIphoneHelpOverlay() : null}

				<div className={css(styles.container)}>
					<Row>
						<Column
							large={6}
							largeOffset={3}
							medium={6}
							mediumOffset={3}
							small={8}
							smallOffset={2}
						>
							<div className={css(styles.certificateBorderTopContainer)}>
								<img alt="title-decoration1" src="/images/heart-bow.png" />
							</div>
						</Column>
					</Row>
					<Row>
						<Column
							large={6}
							largeOffset={3}
							medium={6}
							mediumOffset={3}
							small={8}
							smallOffset={2}
						>
							<div className={css(styles.title)}>{this.strings.CERTIFICATE}</div>
						</Column>
					</Row>
					<Row>
						<Column
							large={6}
							largeOffset={3}
							medium={6}
							mediumOffset={3}
							small={8}
							smallOffset={2}
						>
							<div className={css(styles.certificateBorderBottomContainer)}>
								<img alt="title-decoration2" src="/images/line.png" />
							</div>
						</Column>
					</Row>
					<Row className={css(styles.certificateInfoRow)}>
						<Column large={6} medium={6} small={12}>
							<CertificateInfo
								courseTitle={this.props.courseTitle}
								completionDate={format(this.props.completionDate, "M/D/YYYY")}
								certificateNumber={this.props.certificateNumber}
								className={css(styles.certificateInfo)}
								language={this.props.language}
							/>
						</Column>
						<Column large={6} medium={6} small={12}>
							<div className={css(styles.courseImageContainer)}>
								{this.props.courseImage && (
									<img
										alt="Class"
										src={this.props.courseImage.url1x}
										srcSet={`${this.props.courseImage.url2x} 2x, ${
											this.props.courseImage.url3x
										} 3x`}
									/>
								)}
							</div>
						</Column>
					</Row>
					<Row>
						<Column>
							<div className={css(styles.instructionContainer)}>
								{this.strings.certificateInstructions}
							</div>
						</Column>
					</Row>
					{this.props.resolution &&
						this.props.resolution.length > 0 &&
						this.props.resolutionPrompt &&
						this.props.resolutionPrompt.length > 0 && (
							<Row>
								<Column>
									<CertificateResolution
										prompt={this.props.resolutionPrompt}
										resolution={this.props.resolution}
									/>
								</Column>
							</Row>
						)}
					<Row className={css(styles.buttonRow)}>
						{/* <Column large={4} medium={4} small={12} largeOffset={2} mediumOffset={2}>
							<div className={css(styles.buttonContainer)}>
								<button className={css(styles.button)} onClick={this._checkPlatform}>
									{this.strings.SAVE_PICTURE}
								</button>
							</div>
						</Column> */}
						{/* <Column large={4} medium={4} small={8} smallOffset={2}>
							<div className={css(styles.buttonContainer)}>
								<button className={css(styles.button)} onClick={this._downloaddPdf}>
									{strings.DOWNLOAD_PDF}
								</button>
							</div>
						</Column> */}
						<Column large={4} medium={4} small={12}>
							<div className={css(styles.buttonContainer)}>
								<button className={css(styles.button)} onClick={this._print}>
									{this.strings.PRINT}
								</button>
							</div>
						</Column>
					</Row>
				</div>
			</Container>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/Certificate.js